import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import {
  getApiErrorMessage,
  getMixStockSummary,
} from '../../../../api/mix-deliveriesApi';

export default function MixStockSummary({ refreshKey = 0 }) {
  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getMixStockSummary();

      setData(result);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  if (loading && !data) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 3,
        }}
      >
        <ReplenishmentTable rows={data?.replenishment ?? []} />

        <ShortageTable rows={data?.shortages ?? []} />
      </Box>
    </Stack>
  );
}

function ReplenishmentTable({ rows }) {
  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Uzupełnienie strefy 0D20
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Łączna ilość wymagana dla aktywnych miksów.
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Materiał</TableCell>

              <TableCell>Nazwa</TableCell>

              <TableCell>Skład</TableCell>

              <TableCell align="right">Do uzupełnienia</TableCell>

              <TableCell>JM</TableCell>

              <TableCell align="right">Dostawy</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={[
                  row.materialNumber,
                  row.unit,
                  row.sourceStorageLocation,
                ].join('-')}
                hover
              >
                <TableCell>
                  <Typography fontWeight={700} variant="body2">
                    {row.materialNumber}
                  </Typography>
                </TableCell>

                <TableCell>{row.description}</TableCell>

                <TableCell>
                  <Chip size="small" label={row.sourceStorageLocation} />
                </TableCell>

                <TableCell align="right">
                  <Typography fontWeight={700}>
                    {formatQuantity(row.totalQuantity)}
                  </Typography>
                </TableCell>

                <TableCell>{row.unit}</TableCell>

                <TableCell align="right">{row.deliveryCount}</TableCell>
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  Strefa 0D20 nie wymaga uzupełnienia.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function ShortageTable({ rows }) {
  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Brakujące indeksy
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Materiały, których nie można pokryć z żadnego składu.
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Materiał</TableCell>

              <TableCell>Nazwa</TableCell>

              <TableCell align="right">Brakująca ilość</TableCell>

              <TableCell>JM</TableCell>

              <TableCell align="right">Dostawy</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow key={[row.materialNumber, row.unit].join('-')} hover>
                <TableCell>
                  <Typography fontWeight={700} variant="body2">
                    {row.materialNumber}
                  </Typography>
                </TableCell>

                <TableCell>{row.description}</TableCell>

                <TableCell align="right">
                  <Typography fontWeight={700} color="error.main">
                    {formatQuantity(row.totalShortageQuantity)}
                  </Typography>
                </TableCell>

                <TableCell>{row.unit}</TableCell>

                <TableCell align="right">{row.deliveryCount}</TableCell>
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  Brak brakujących indeksów.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function formatQuantity(value) {
  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(Number(value ?? 0));
}
